/**
 * Otto — "chat with your data" analytics agent. The FE asks a natural-language
 * question; operations turns it into a read-only SQL query, runs it under the
 * tenant-locked `otto_ro` role, and returns a saved, exportable report.
 *
 * All calls go through `operationsClient` (Bearer + x-tenant-id injected
 * automatically) at the un-prefixed `/otto` routes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, operationsClient } from '@/lib/api';

const BASE = '/otto';

export interface OttoColumn {
  key: string;
  label: string;
  type: string;
}

export type OttoRow = Record<string, unknown>;

export interface OttoReport {
  id: string;
  question: string;
  sql: string;
  explanation: string | null;
  columns: OttoColumn[];
  rows: OttoRow[];
  rowCount: number;
  truncated: boolean;
  status: 'succeeded' | 'failed' | string;
  errorMessage: string | null;
  createdAt: string;
}

export interface OttoReportSummary {
  id: string;
  question: string;
  rowCount: number;
  status: string;
  createdAt: string;
}

/** /otto/ask returns either a report to render or a plain-text answer.
 * `followUps` are suggested next questions shown as clickable chips. */
export type OttoAskResponse =
  | { kind: 'report'; report: OttoReport; followUps: string[] }
  | { kind: 'answer'; text: string };

export interface OttoSuggestions {
  intro: string;
  examples: string[];
}

export interface OttoHistoryMessage {
  role: 'user' | 'otto';
  content: string;
}

export const ottoKeys = {
  status: ['otto', 'status'] as const,
  suggestions: ['otto', 'suggestions'] as const,
  reports: ['otto', 'reports'] as const,
  report: (id: string) => ['otto', 'report', id] as const,
};

export interface OttoStatus {
  /** A connection string and an LLM key are configured. */
  enabled: boolean;
  /** Configured AND the database answered a probe; absent in legacy responses. */
  ready?: boolean;
  database?: { reachable: boolean; error?: string };
}

/**
 * Whether Otto can actually answer questions here. `enabled` is configuration
 * alone, which once reported a healthy Otto for six days while its database was
 * unreachable — read `ready` to decide, and `database.error` to say why not.
 */
export function useOttoStatus() {
  return useQuery({
    queryKey: ottoKeys.status,
    queryFn: () => apiGet<OttoStatus>(operationsClient, `${BASE}/status`),
    staleTime: 30_000,
    // A configured service can recover while this screen stays open. Stop
    // polling once ready; missing setup needs configuration, not more probes.
    refetchInterval: (query) =>
      query.state.data?.enabled === true && query.state.data.ready === false ? 30_000 : false,
  });
}

/** Intro + example questions grounded in the tenant's schema. */
export function useOttoSuggestions(enabled: boolean) {
  return useQuery({
    queryKey: ottoKeys.suggestions,
    queryFn: () => apiGet<OttoSuggestions>(operationsClient, `${BASE}/suggestions`),
    enabled,
    staleTime: 30 * 60_000,
  });
}

/** Ask Otto. Resolves to a report (data question) or a text answer (help). */
export function useAskOtto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { question: string; history: OttoHistoryMessage[] }) =>
      apiPost<OttoAskResponse, { question: string; history: OttoHistoryMessage[] }>(
        operationsClient,
        `${BASE}/ask`,
        vars,
      ),
    onSuccess: (res) => {
      if (res.kind === 'report') {
        qc.invalidateQueries({ queryKey: ottoKeys.reports });
        qc.setQueryData(ottoKeys.report(res.report.id), res.report);
      }
    },
  });
}

export function useOttoReports() {
  return useQuery({
    queryKey: ottoKeys.reports,
    queryFn: () => apiGet<OttoReportSummary[]>(operationsClient, `${BASE}/reports`),
  });
}

/** Fetch a saved report. Shared by the query hook and imperative click-to-load. */
export function fetchOttoReport(id: string): Promise<OttoReport> {
  return apiGet<OttoReport>(operationsClient, `${BASE}/reports/${id}`);
}

export function useOttoReport(id: string | undefined) {
  return useQuery({
    queryKey: ottoKeys.report(id ?? ''),
    queryFn: () => fetchOttoReport(id!),
    enabled: !!id,
  });
}

/** Stream a report export (CSV/XLSX) and trigger a browser download. */
export async function downloadOttoReport(id: string, format: 'csv' | 'xlsx'): Promise<void> {
  const response = await operationsClient.get(`${BASE}/reports/${id}/export?format=${format}`, {
    responseType: 'blob',
  });
  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `otto-report-${id}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}
